import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Briefcase, ChevronRight, BadgePercent, UserCog, FileText, Truck, Send, Loader2, CheckCircle2, Package, Headset, LogIn, Clock } from "lucide-react";
import api, { apiErrorMessage } from "../services/api";
import { useAuth } from "../context/AuthContext";

const ADVANTAGES = [
  { icon: BadgePercent, title: "Tarifs professionnels", desc: "Remises dégressives selon vos volumes, négociées pour les garages et ateliers." },
  { icon: UserCog, title: "Compte dédié", desc: "Un interlocuteur commercial unique qui connaît votre activité et vos besoins." },
  { icon: FileText, title: "Facturation & suivi", desc: "Factures HT téléchargeables, historique complet et paiement à échéance." },
  { icon: Truck, title: "Livraison prioritaire", desc: "Expédition express en 24/48h pour ne jamais immobiliser un véhicule." },
  { icon: Package, title: "Catalogue étendu", desc: "Accès à des milliers de références OE et équivalentes, disponibilité en temps réel." },
  { icon: Headset, title: "Support technique", desc: "Une équipe qui vous aide à identifier la bonne pièce, rapidement." },
];

const ProfessionnelsPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company: "", siret: "", phone: "", message: "" });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company.trim()) { setError("Le nom de la société est requis."); return; }
    if (!accepted) { setError("Vous devez accepter les conditions du compte professionnel."); return; }
    setLoading(true); setError("");
    try {
      await api.post("/pro/request", {
        company: form.company, siret: form.siret, phone: form.phone, message: form.message,
        accepted_terms: accepted,
      });
      await refreshUser();
      setSent(true);
    } catch (err) {
      setError(apiErrorMessage(err, "Une erreur est survenue. Veuillez réessayer."));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="pro-page">
      <section className="hero-swoosh">
        <div className="max-w-[1100px] mx-auto px-5 pt-8 pb-8 relative z-10">
          <div className="flex items-center gap-1.5 text-[14px] text-slate-400 mb-4">
            <Link to="/" className="hover:text-[#FF3B3B]">Accueil</Link>
            <ChevronRight size={15} />
            <span className="text-slate-50 font-semibold">Professionnels</span>
          </div>
          <span className="inline-block bg-[#E01F1F] text-white text-[12px] font-bold px-3 py-1 rounded-full mb-3">ESPACE PRO</span>
          <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-3xl md:text-5xl mb-2 flex items-center gap-3">
            <Briefcase className="text-[#FF3B3B]" size={40} /> KTSauto Professionnels
          </h1>
          <p className="text-slate-400 max-w-2xl">Garages, ateliers, carrossiers et revendeurs : profitez de tarifs dédiés, d'un compte pro sur mesure et d'une logistique pensée pour votre activité.</p>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {ADVANTAGES.map((a) => (
            <div key={a.title} data-testid={`pro-advantage-${a.title}`} className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-5 hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-xl bg-[#10131A] flex items-center justify-center mb-3">
                <a.icon size={22} className="text-[#FF3B3B]" />
              </div>
              <div className="font-bold text-slate-50 text-[15px] mb-1">{a.title}</div>
              <div className="text-[13px] text-slate-400 leading-relaxed">{a.desc}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-2">
            <h2 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl md:text-3xl mb-3">Ouvrez votre compte pro</h2>
            <p className="text-slate-300 text-[15px] leading-relaxed mb-4">Remplissez le formulaire : notre équipe commerciale vous recontacte sous 24h ouvrées pour activer votre compte professionnel et vous transmettre vos conditions tarifaires.</p>
            <ul className="space-y-2 text-[14px] text-slate-300">
              <li className="flex items-center gap-2"><CheckCircle2 size={17} className="text-[#FF3B3B]" /> Sans engagement</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={17} className="text-[#FF3B3B]" /> Activation rapide</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={17} className="text-[#FF3B3B]" /> Réservé aux professionnels (SIRET)</li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            {!user ? (
              <div data-testid="pro-login-required" className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-8 text-center">
                <LogIn size={40} className="mx-auto text-[#FF3B3B] mb-3" />
                <div className="font-extrabold italic uppercase tracking-tight text-slate-50 text-xl mb-1">Connectez-vous pour continuer</div>
                <p className="text-slate-300 mb-5">La demande de compte professionnel est rattachée à votre compte client. Connectez-vous ou créez un compte gratuit.</p>
                <div className="flex items-center justify-center gap-3">
                  <button data-testid="pro-login-btn" onClick={() => navigate("/connexion")} className="btn-orange rounded-lg px-6 py-3 font-bold">Se connecter / S'inscrire</button>
                </div>
              </div>
            ) : user.is_pro ? (
              <div data-testid="pro-already" className="bg-emerald-950/40 border border-emerald-800/50 rounded-2xl p-10 text-center">
                <CheckCircle2 size={44} className="mx-auto text-emerald-400 mb-3" />
                <div className="font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl mb-1">Vous êtes client Pro !</div>
                <p className="text-slate-300">Votre remise professionnelle de <strong>{user.effective_pro_discount}%</strong> est appliquée automatiquement sur tout le catalogue.</p>
              </div>
            ) : (sent || user.pro_status === "pending") ? (
              <div data-testid="pro-pending" className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-10 text-center">
                <Clock size={44} className="mx-auto text-[#FF3B3B] mb-3" />
                <div className="font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl mb-1">Demande en cours d'examen</div>
                <p className="text-slate-300">Merci ! Notre équipe commerciale étudie votre demande et vous recontacte sous 24h ouvrées pour activer votre compte pro.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Société *</label>
                    <input data-testid="pro-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="w-full border-2 border-[#222735] rounded-lg px-3 py-3 outline-none focus:border-[#E01F1F] text-slate-50" placeholder="Nom de votre entreprise" />
                  </div>
                  <div>
                    <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">SIRET</label>
                    <input data-testid="pro-siret" value={form.siret} onChange={(e) => set("siret", e.target.value)} className="w-full border-2 border-[#222735] rounded-lg px-3 py-3 outline-none focus:border-[#E01F1F] text-slate-50" placeholder="14 chiffres" />
                  </div>
                </div>
                <div>
                  <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Téléphone</label>
                  <input data-testid="pro-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full border-2 border-[#222735] rounded-lg px-3 py-3 outline-none focus:border-[#E01F1F] text-slate-50" placeholder="06 12 34 56 78" />
                </div>
                <div>
                  <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Votre activité / besoins</label>
                  <textarea data-testid="pro-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} className="w-full border-2 border-[#222735] rounded-lg px-3 py-3 outline-none focus:border-[#E01F1F] text-slate-50 resize-y" placeholder="Décrivez votre activité, vos volumes, vos marques préférées…" />
                </div>
                {error && <p data-testid="pro-error" className="text-red-400 text-[14px] font-medium">{error}</p>}
                <label className="flex items-start gap-2.5 text-[13px] text-slate-300 cursor-pointer">
                  <input data-testid="pro-accept-terms" type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#E01F1F] shrink-0" />
                  <span>Je reconnais avoir lu et accepté les <Link to="/professionnels/conditions" target="_blank" rel="noopener noreferrer" className="text-[#FF3B3B] font-semibold hover:underline">conditions du compte professionnel</Link> et les <Link to="/professionnels/conditions" target="_blank" rel="noopener noreferrer" className="text-[#FF3B3B] font-semibold hover:underline">conditions tarifaires pro</Link>.</span>
                </label>
                <button data-testid="pro-submit" type="submit" disabled={loading || !accepted} className="btn-orange rounded-lg px-8 py-3.5 font-bold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} Demander mon compte pro
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfessionnelsPage;
